import {useEffect, useCallback, ReactElement, useState} from "react";
import {connect} from "react-redux";
import {AppState, select} from "../../store";
import {UserDto} from "../../utils/types/user";
import {FacilityModel} from "../../utils/types/facility";

declare const window: any;

// Builds comma-separated string of facility names for Stonly tracking
const buildFacilities = (facilities?: ReadonlyArray<FacilityModel>) =>
  facilities.reduce(
    (currentList, facility) => `${currentList}${facility.name},`,
    "",
  );

// Stonly Widget Configuration Component - handles widget initialization and user identification
const Stonly = ({user}: {user: UserDto}): ReactElement => {
  const [widgetError, setWidgetError] = useState(false);

  // Initializes Stonly widget with error handling and polling for availability
  const initStonly = async () => {
    const originalErrorHandler = window.onerror;
    let checkTimeout: NodeJS.Timeout | null = null;
    let isDestroyed = false;

    // Cleanup function to restore handlers and clear timeouts
    const cleanup = () => {
      window.onerror = originalErrorHandler;
      if (checkTimeout) {
        clearTimeout(checkTimeout);
        checkTimeout = null;
      }
    };

    try {
      const {stonlyWidget} = await import("./stonlyscripts");
      window.STONLY_WID = "7addaf56-4298-11ec-83f1-062882f67cfe";

      // Handle Stonly-specific JSON parsing errors
      window.onerror = (
        event: Event | string,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error,
      ) => {
        if (
          error?.message === "Unexpected end of JSON input" &&
          source?.includes("stonly.com")
        ) {
          setWidgetError(true);
          cleanup();
          return true;
        }
        return (
          originalErrorHandler?.(event, source, lineno, colno, error) ?? false
        );
      };

      // Initialize widget (loads script asynchronously)
      stonlyWidget(
        window,
        document,
        "script",
        "https://stonly.com/js/widget/v2/",
      );

      // Poll for widget with exponential backoff
      let attempts = 0;
      const maxAttempts = 50; // 5 second timeout

      const checkWidget = () => {
        if (isDestroyed) return;
        attempts++;

        if (window.StonlyWidget?._api) {
          window.StonlyWidget("setYOffset", 0);
          cleanup();
          return;
        }

        if (attempts >= maxAttempts) {
          // eslint-disable-next-line no-console
          console.warn("Stonly widget failed to load within timeout");
          setWidgetError(true);
          cleanup();
          return;
        }

        // Exponential backoff: 50ms to 200ms
        const delay = Math.min(50 * Math.pow(1.2, attempts), 200);
        checkTimeout = setTimeout(checkWidget, delay);
      };

      checkTimeout = setTimeout(checkWidget, 100);

      return () => {
        isDestroyed = true;
        cleanup();
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to initialize Stonly widget:", error);
      setWidgetError(true);
      cleanup();
      // Return no-op cleanup function to satisfy return type consistency
      return () => {};
    }
  };

  // Identifies user to Stonly for personalized help content
  const stonlyIdentify = useCallback(async () => {
    if (widgetError) return;

    try {
      const {stonlyTracking} = await import("./stonlyscripts");

      stonlyTracking(
        window,
        document,
        "script",
        "https://stonly.com/js/tracker/stn.js",
      );

      window.stonlyTrack("init", window.STONLY_WID);
      window.stonlyTrack("identify", user.id, {
        "logged-in": true,
        role: user.role.name.toString(),
        organization: user?.organization?.name ?? "Reside",
        facilities: buildFacilities(user.facilities),
        email: user.email,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to initialize Stonly tracking:", error);
      setWidgetError(true);
    }
  }, [user, widgetError]);

  // Initialize widget on mount
  useEffect(() => {
    let stonlyCleanup: (() => void) | undefined;

    const init = async () => {
      stonlyCleanup = await initStonly();
    };

    init();

    return () => {
      stonlyCleanup?.();
      if (window.StonlyWidget) {
        window.StonlyWidget("closeWidget");
        window.StonlyWidget("setYOffset", -100);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Identify user when available
  useEffect(() => {
    if (user && !widgetError) {
      stonlyIdentify();

      return () => {
        if (window.StonlyWidget && window.stonlyTrack) {
          window.StonlyWidget("closeWidget");
          window.StonlyWidget("setYOffset", -100);
          window.stonlyTrack("identify", null, {
            "logged-in": false,
            role: "",
            organization: "",
            facilities: "",
          });
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, widgetError]);

  return null;
};

const mapStateToProps = (state: AppState) => ({
  user: select.adminSession.user(state),
});

export const StonlyConfig = connect(mapStateToProps)(Stonly);
